import {ComponentLink, Link, makeChain, valueHelper as value}   from "@Common/react/cst2";
import {Load2}                                                  from "@Common/react/load2";
import {Types}                                                  from "@Common/types";
import {AlertModal}                                            from "@Common/ui-components/heavy/common/design2/dialogs/alert-modal/alert-modal";
import {sort as sorted}                                         from "@Common/utils/collections";
import {chain}                                                  from "@Common/utils/fs";

import {consumeContext}             from "@Xpresso/loaders/consume-context";
import {AGENT_LEVEL, COMPANY_LEVEL} from "@Xpresso/routes/common/logic/user-level";

type Item = Types.Apis.Payloads.Next.Item.GetPending.Item;

export const PendingOrdersTableState = ({next: rootNext, searchQuery, sortMode}:{
    next        : Link<JSX.Element, {
        error         : boolean,
        loading       : boolean,
        onItemDeleted : (obj:{
            orderId: string,
            cartId: number,
        }) => void,
        reload        : () => void,
        rows          : Types.Apis.Payloads.Next.Item.GetPending.Item[],
    }>,
    searchQuery : string,
    sortMode    : {id: string, ascending: boolean},
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("userLevels"))
    .then(...consumeContext("apis"))
    .then(...consumeContext("cart"))
    .then(...consumeContext("menus"))
    .then("alertModal", ({}, next:ComponentLink<typeof AlertModal>) => AlertModal({next}))
    .then("pendingItems", ({apis}, next:ComponentLink<typeof Load2<Types.Apis.Payloads.Next.Item.GetPending.Response>>) => (
        Load2({
            fetch: () => apis.next.item.getPending(),
            next,
        })
    ))
    .then("filteredItems", value(
        ({pendingItems, userLevels}) => pendingItems.value?.filter?.(makeOrderFilter(searchQuery, userLevels))
    ))
    .then(({apis, cart, alertModal: {show: alert}, filteredItems, pendingItems: {change: changePendingItems, loading, reload}}) => (
        rootNext({
            loading,
            reload,
            error         : !filteredItems,
            onItemDeleted : async ({orderId, cartId}) => {
                alert({message: "Order deleted"});
                changePendingItems((orders) => orders.filter((row) => row.orderId !== orderId))
                if(cart.value?.cartId && cart.value.cartId === cartId) {
                    cart.onChange(await apis.next.cart.getCurrentCart());
                }
            },
            rows          : chain(
                sorted(filteredItems ?? [], (order) => {
                    let sortValue:any;
                    const DEFAULT_SORT_MODE = order.date.date;
                    const SORT_MODES = {
                        agentName  : order.agentNameFirst,
                        branchName : order.branchName,
                        cartId     : order.idCart,
                        date       : order.date.date,
                        orderId    : order.orderId,
                    }

                    sortValue = SORT_MODES?.[sortMode.id] ?? DEFAULT_SORT_MODE;

                    // always sort falsies last
                    return (sortValue || (sortMode.ascending ? String.fromCharCode(65535) : "")).toString();
                }),
                (sortedItems:any[]) => sortMode.ascending ? sortedItems : sortedItems.reverse()
            ),
        })
    ))
    .run()
);

const makeOrderFilter = (search:string, userLevels: Types.Apis.Payloads.Next.Session.UserLevel.Response) => {
    const searchLC = search.toLowerCase();
    return (order:Item) => [
        order.orderId,
        order.idCart,
        order.orderName,
        ...(userLevels.userLevel != AGENT_LEVEL ? [
            order.agentNameFirst + ' ' + order.agentNameLast
        ] : []),
        ...(userLevels.userLevel == COMPANY_LEVEL ? [
            order.branchName
        ] : []),
        ...(userLevels.internalUser ? [
            order.idBranch,
            order.idAgent
        ] : []),
    ].filter(v=>v).some(val => String(val).toLowerCase().includes(searchLC))
};

